<template>
  <Layout
    class="onboarding-summary"
    :title="page.title"
    :scrollDefaultSlot="true"
  >
    <SummarySection
      v-if="summaryWhatPrevents"
      title="What stops me from reading more"
      class="onboarding-summary__section onboarding-summary__section--what-prevents-you"
    >
      <SummaryChoices
        pageID="what-prevents-you"
        :userChoices="summaryWhatPrevents"
        :view="view"
      />
    </SummarySection>

    <Divider v-if="summaryWhatPrevents" class="onboarding-summary__divider" />

    <SummarySection
      v-if="summaryGenreChoices"
      title="The main areas I want to improve in"
      class="onboarding-summary__section onboarding-summary__section--genres"
    >
      <SummaryChoices
        pageID="genre-selection"
        :userChoices="summaryGenreChoices"
        :view="view"
      />
    </SummarySection>

    <Divider v-if="summaryGenreChoices" class="onboarding-summary__divider" />

    <SummarySection
      v-if="bookChoices"
      title="Your top books"
      class="onboarding-summary__section onboarding-summary__section--books"
    >
      <li
        v-for="(book, i) of bookChoices.slice(0, 5)"
        :key="book.id"
        class="onboarding-summary__book"
      >
        <img
          :src="cacheBust(book.cover)"
          alt=""
          width="158"
          height="211"
          class="onboarding-summary__cover"
        />
        <span
          v-if="i === 4 && bookChoices.length > 5"
          class="onboarding-summary__more-books"
        >
          +{{ bookChoices.length - 5 }}
        </span>
      </li>
    </SummarySection>
  </Layout>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { QuestionnaireView } from '@/models/onboarding'
import type {
  BookSelectionChoice,
  SummaryPage,
} from '@/models/onboarding.types'
import { cacheBust } from '@/models/doc'

import Divider from '@/components/ui/Divider.vue'
import Layout from '../Layout.vue'
import SummarySection from '../SummarySection.vue'
import SummaryChoices from '../SummaryChoices.vue'

const props = defineProps<{
  view: QuestionnaireView
}>()

const page = computed(() => {
  return props.view.currentPage as SummaryPage
})

const summaryWhatPrevents = computed(() => {
  const choices = props.view.getUserChoice<string[]>('what-prevents-you')
  return choices?.length ? choices : undefined
})

const summaryGenreChoices = computed(() => {
  const choices = props.view.getUserChoice<string[]>('genre-selection')
  return choices?.length ? choices : undefined
})

const bookChoices = computed(() => {
  return props.view.getUserChoice<BookSelectionChoice>('book-selection')
})
</script>

<style lang="scss" scoped>
.onboarding-summary {
  overflow-x: hidden;

  &:deep(.onboarding-layout__default-slot) {
    display: grid;
    justify-content: center;
    gap: 20px;
    width: calc(100% + var(--onboarding-padding) * 2);
    max-height: fit-content;
    padding-inline: var(--onboarding-padding);
    padding-top: var(--onboarding-padding);
  }

  &__divider {
    @media (max-width: 400px) {
      width: calc(100% + var(--onboarding-padding) * 2);
      max-width: none;
      margin-left: calc(var(--onboarding-padding) * -1);
    }

    &:last-child {
      display: none;
    }
  }

  &__section--books {
    &:deep(.summary-section__choices) {
      gap: 0;
      align-items: center;
      justify-content: center;
      padding-top: 8px;
    }
  }

  &__book {
    position: relative;
    flex-shrink: 0;
    margin-top: -8px;
    margin-left: -8px;
    border-radius: 8px;
    border: 2px solid $c-white;
    filter: drop-shadow(-5px 4px 13px rgba(0, 0, 0, 0.15))
      drop-shadow(0 2px 8px rgba(0, 0, 0, 0.11));

    &:first-child {
      margin-left: 0;
    }
  }

  &__cover {
    width: auto;
    height: 80px;
    max-width: none;
  }

  &__more-books {
    position: absolute;
    right: -14px;
    top: calc(50% - 14px);
    display: flex;
    justify-content: center;
    align-items: center;
    width: 28px;
    height: 28px;
    font-size: 14px;
    font-weight: 500;
    background: $c-white;
    border-radius: 28px;
    filter: drop-shadow(-5px 4px 13px rgba(0, 0, 0, 0.15))
      drop-shadow(0 2px 8px rgba(0, 0, 0, 0.11));
  }
}
</style>
